import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Holding, SortKey, SortDir, Transaction, PriceMap } from '../../types';
import { CardHeader } from '../ui/Card';
import { PnlBadge } from '../ui/Badge';
import { formatAmount, formatPercent } from '../../utils/formatters';
import { useCurrency } from '../../context/CurrencyContext';
import { useCoinList } from '../../hooks/useCoinList';
import { loadCoinImages } from '../../utils/coinImageCache';
import { isMetal, METAL_DISPLAY } from '../../utils/metals';
import { isStockOrETF, isETF, ASSET_BADGE } from '../../utils/stocks';
import { ChevronUp, ChevronDown, ChevronsUpDown, Coins, ExternalLink, TrendingUp, Wallet, Lock } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useUpgradePrompt } from '../../context/UpgradePromptContext';
import { FEATURE_GATES } from '../../config/limits';
import { ALL_WALLETS, UNSPECIFIED, WalletFilterState } from '../../hooks/useWalletFilter';

interface HoldingsTableProps {
  holdings: Holding[];
  transactions?: Transaction[];
  prices?: PriceMap;
  /**
   * The Dashboard's shared wallet filter (useWalletFilter). Lifted so the
   * Portfolio Value chart and Asset Allocation follow the same selection.
   */
  wallet: WalletFilterState;
  hideAllocation?: boolean;
}

type Col = { key: SortKey; label: string; align: 'left' | 'right' };

const COLS: Col[] = [
  { key: 'name',         label: 'Asset',      align: 'left'  },
  { key: 'amount',       label: 'Amount',      align: 'right' },
  { key: 'avgBuyPrice',  label: 'Avg Buy',     align: 'right' },
  { key: 'currentPrice', label: 'Current',     align: 'right' },
  { key: 'change24h',    label: '24h',         align: 'right' },
  { key: 'totalValue',   label: 'Value',       align: 'right' },
  { key: 'pnl',          label: 'P&L',         align: 'right' },
  { key: 'allocation',   label: 'Allocation',  align: 'right' },
];

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-600" />;
  return dir === 'asc'
    ? <ChevronUp className="w-3.5 h-3.5 text-slate-100" />
    : <ChevronDown className="w-3.5 h-3.5 text-slate-100" />;
}

/** Asset avatar — metal circle, stock/ETF circle, crypto image, or a coloured
 *  fallback. Shared by the desktop table row and the mobile card. */
function AssetIcon({ h, info }: { h: Holding; info?: { image?: string } }) {
  if (isMetal(h.coinId)) {
    return (
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{ backgroundColor: METAL_DISPLAY[h.coinId].bg, border: `1.5px solid ${METAL_DISPLAY[h.coinId].border}` }}
      >
        <span style={{ color: METAL_DISPLAY[h.coinId].color }}>{METAL_DISPLAY[h.coinId].label}</span>
      </div>
    );
  }
  if (isStockOrETF(h.coinId)) {
    return (
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: isETF(h.coinId) ? '#8B5CF615' : '#3B82F615',
          border: `1.5px solid ${isETF(h.coinId) ? '#8B5CF640' : '#3B82F640'}`,
        }}
      >
        <TrendingUp className="w-4 h-4" style={{ color: isETF(h.coinId) ? '#8B5CF6' : '#3B82F6' }} />
      </div>
    );
  }
  if (info?.image) {
    return (
      <img
        src={info.image}
        alt={h.name}
        className="w-9 h-9 rounded-full flex-shrink-0"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
      style={{ backgroundColor: h.color + '30', border: `1.5px solid ${h.color}50` }}
    >
      <span style={{ color: h.color }}>{h.symbol.slice(0, 2)}</span>
    </div>
  );
}

export function HoldingsTable({ holdings, transactions, prices, wallet, hideAllocation }: HoldingsTableProps) {
  const { canPlan } = useSubscription();
  const { open: openUpgradePrompt } = useUpgradePrompt();
  const [sortKey, setSortKey] = useState<SortKey>('totalValue');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const { fmt, fmtDisplay } = useCurrency();
  const {
    walletFilter, setWalletFilter, walletOptions, hasUnspecified, canFilter,
    walletLabel: filterLabel, walletHoldings: displayHoldings,
  } = wallet;
  const { coins } = useCoinList();
  const navigate  = useNavigate();

  // Build coinInfoMap from top-200, then backfill image + basic info for any
  // holdings whose coin is outside the top-200 using the persisted image cache.
  const coinInfoMap = useMemo(() => {
    const map: Record<string, typeof coins[0]> = Object.fromEntries(coins.map((c) => [c.id, c]));
    const cachedImages = loadCoinImages();
    for (const h of displayHoldings) {
      if (!map[h.coinId] && cachedImages[h.coinId]) {
        map[h.coinId] = {
          id:            h.coinId,
          symbol:        h.symbol,
          name:          h.name,
          image:         cachedImages[h.coinId],
          marketCapRank: 0,
          currentPrice:  h.currentPrice,
          marketCap:     0,
          volume24h:     0,
          high24h:       0,
          low24h:        0,
        };
      }
    }
    return map;
  }, [coins, displayHoldings]);

  // Map coinId → unique wallets from transactions (includes transfer endpoints)
  const walletsMap = useMemo(() => {
    if (!transactions) return {} as Record<string, string[]>;
    const map: Record<string, string[]> = {};
    const add = (coinId: string, w: string) => {
      if (!map[coinId]) map[coinId] = [];
      if (!map[coinId].includes(w)) map[coinId].push(w);
    };
    transactions.forEach((tx) => {
      if (tx.type === 'TRANSFER') {
        if (tx.fromWallet) add(tx.coinId, tx.fromWallet);
        if (tx.toWallet)   add(tx.coinId, tx.toWallet);
        return;
      }
      if (tx.wallet) add(tx.coinId, tx.wallet);
    });
    return map;
  }, [transactions]);

  const showWallets = !!transactions;
  const visibleCols = hideAllocation ? COLS.filter((c) => c.key !== 'allocation') : COLS;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = [...displayHoldings].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    const cmp  = typeof aVal === 'string'
      ? (aVal as string).localeCompare(bVal as string)
      : (aVal as number) - (bVal as number);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const filterDropdown = canFilter ? (
    <div className="relative flex items-center">
      <Wallet className="absolute left-3 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
      <select
        value={walletFilter}
        onChange={(e) => setWalletFilter(e.target.value)}
        aria-label="Filter holdings by wallet"
        className="bg-slate-800 border border-slate-700 rounded-xl pl-8 pr-3 py-2 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors cursor-pointer [color-scheme:dark]"
      >
        <option value={ALL_WALLETS}>All Portfolios</option>
        {walletOptions.map((w) => (
          <option key={w} value={w}>{w}</option>
        ))}
        {hasUnspecified && <option value={UNSPECIFIED}>Unspecified</option>}
      </select>
    </div>
  ) : null;

  if (holdings.length === 0) {
    return (
      <div>
        <CardHeader title="Holdings" subtitle="Your current positions" action={filterDropdown} />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Coins className="w-6 h-6 text-slate-700 mb-3" />
          <p className="text-slate-500 text-sm">No holdings yet</p>
          <p className="text-[12px] text-[#444444] mt-1">Add a BUY transaction to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <CardHeader
        title="Holdings"
        subtitle={
          filterLabel
            ? `${displayHoldings.length} asset${displayHoldings.length !== 1 ? 's' : ''} in ${filterLabel} · click crypto for details`
            : `${displayHoldings.length} asset${displayHoldings.length !== 1 ? 's' : ''} · click crypto for details`
        }
        action={filterDropdown}
      />
      {/* Desktop: wide table (md and up). Mobile uses the card list below. */}
      <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-slate-700">
                {visibleCols.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`px-2 py-3 cursor-pointer select-none transition-colors ${col.align === 'right' ? 'text-right' : 'text-left'} first:pl-0 last:pr-0`}
                    style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-label)', fontWeight: 400 }}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.align === 'right' && <SortIcon active={sortKey === col.key} dir={sortDir} />}
                      {col.label}
                      {col.align === 'left'  && <SortIcon active={sortKey === col.key} dir={sortDir} />}
                    </span>
                  </th>
                ))}
                {showWallets && (
                  <th className="px-2 py-3 pr-0 text-left" style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-label)', fontWeight: 400 }}>
                    Wallets
                  </th>
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-700/40">
              {sorted.map((h) => {
                const info = coinInfoMap[h.coinId];
                // Metal with no entry in the (merged) price map → price unavailable
                // (the hook omits unavailable metals rather than pricing them at 0).
                const priceUnavailable = isMetal(h.coinId) && !prices?.[h.coinId];
                return (
                  <tr
                    key={h.coinId}
                    onClick={() => {
                      if (!isMetal(h.coinId) && !isStockOrETF(h.coinId)) {
                        navigate(`/coin/${h.coinId}`, { state: { from: 'dashboard' } });
                      }
                    }}
                    className={`hover:bg-slate-800/30 transition-colors group ${!isMetal(h.coinId) && !isStockOrETF(h.coinId) ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    {/* Asset */}
                    <td className="px-2 py-4 pl-0">
                      <div className="flex items-center gap-3.5">
                        <AssetIcon h={h} info={info} />
                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-medium text-slate-100 text-[14px]">{h.name}</p>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ASSET_BADGE[h.assetType].className}`}>
                              {ASSET_BADGE[h.assetType].label}
                            </span>
                            {!isMetal(h.coinId) && !isStockOrETF(h.coinId) && (
                              <ExternalLink className="w-3 h-3 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{h.symbol}</p>
                        </div>
                      </div>
                    </td>

                    {/* Amount */}
                    <td className="px-2 py-4 text-right">
                      <span className="text-[14px] font-mono text-slate-300">{formatAmount(h.amount)}</span>
                    </td>

                    {/* Avg Buy */}
                    <td className="px-2 py-4 text-right">
                      <span className="text-[14px] text-slate-400">{fmtDisplay(h.display.avgBuyPrice)}</span>
                    </td>

                    {/* Current Price */}
                    <td className="px-2 py-4 text-right">
                      {priceUnavailable
                        ? <span className="text-[14px] text-slate-600">—</span>
                        : <span className="text-[14px] font-medium text-slate-200">{fmt(h.currentPrice)}</span>}
                    </td>

                    {/* 24h Change */}
                    <td className="px-2 py-4 text-right">
                      {priceUnavailable
                        ? <span className="text-[14px] text-slate-600">—</span>
                        : <span className={`text-[14px] font-medium ${h.change24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatPercent(h.change24h)}
                          </span>}
                    </td>

                    {/* Total Value */}
                    <td className="px-2 py-4 text-right">
                      {priceUnavailable
                        ? <span className="text-[14px] text-slate-600">—</span>
                        : <span className="text-[14px] font-semibold text-slate-100">{fmt(h.totalValue)}</span>}
                    </td>

                    {/* P&L */}
                    <td className={`px-2 py-4 text-right ${hideAllocation && !showWallets ? 'pr-0' : ''}`}>
                      {priceUnavailable ? (
                        <span className="text-[14px] text-slate-600">—</span>
                      ) : (
                        <div className="flex flex-col items-end gap-1">
                          <span className={`text-[14px] font-medium ${h.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {h.display.pnl >= 0 ? '+' : ''}{fmtDisplay(h.display.pnl)}
                          </span>
                          <PnlBadge value={h.display.pnlPercent} />
                        </div>
                      )}
                    </td>

                    {/* Allocation */}
                    {!hideAllocation && (
                      <td className={`px-2 py-4 text-right ${!showWallets ? 'pr-0' : ''}`}>
                        {priceUnavailable ? (
                          <span className="text-[14px] text-slate-600">—</span>
                        ) : (
                          <div className="flex flex-col items-end gap-1.5">
                            <span className="text-[14px] text-slate-300">{h.allocation.toFixed(1)}%</span>
                            <div className="w-16 h-1 bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{ width: `${Math.min(h.allocation, 100)}%`, backgroundColor: h.color }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    )}

                    {/* Wallets — per-coin multi-wallet allocation needs the planning tier */}
                    {showWallets && (
                      <td className="px-2 py-4 pr-0">
                        {!canPlan && (walletsMap[h.coinId]?.length ?? 0) > 0 ? (
                          // Free: gate the per-coin wallet split behind an Upgrade CTA.
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openUpgradePrompt(FEATURE_GATES.multiWallet); }}
                            title="Multi-wallet allocation is a Plus feature"
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none whitespace-nowrap transition-colors"
                            style={{ background: 'rgba(212,180,131,0.12)', border: '0.5px solid rgba(212,180,131,0.32)', color: '#b89968' }}
                          >
                            <Lock className="w-2.5 h-2.5" />
                            Pro
                          </button>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(walletsMap[h.coinId] ?? []).map((w) => (
                              <span
                                key={w}
                                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700/60 border border-slate-600/40 text-slate-400 font-medium leading-none whitespace-nowrap"
                              >
                                {w}
                              </span>
                            ))}
                            {!(walletsMap[h.coinId]?.length) && (
                              <span className="text-xs text-slate-600">—</span>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
      </div>

      {/* Mobile: divided list (below md) — no boxes, thin dividers between rows,
          mirroring the desktop table's row separators. Same data + helpers. */}
      <div className="md:hidden divide-y divide-slate-700/40">
        {sorted.map((h) => {
          const info = coinInfoMap[h.coinId];
          const priceUnavailable = isMetal(h.coinId) && !prices?.[h.coinId];
          const isNavigable = !isMetal(h.coinId) && !isStockOrETF(h.coinId);
          const wallets = walletsMap[h.coinId] ?? [];
          return (
            <div
              key={h.coinId}
              onClick={() => {
                if (isNavigable) navigate(`/coin/${h.coinId}`, { state: { from: 'dashboard' } });
              }}
              className={`py-4 transition-colors ${
                isNavigable ? 'cursor-pointer active:bg-slate-800/30' : 'cursor-default'
              }`}
            >
              {/* Top row: identity (left) · value + P&L (right) */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <AssetIcon h={h} info={info} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-medium text-slate-100 text-[14px]">{h.symbol}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ASSET_BADGE[h.assetType].className}`}>
                        {ASSET_BADGE[h.assetType].label}
                      </span>
                      {isNavigable && <ExternalLink className="w-3 h-3 text-slate-600 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{h.name}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {priceUnavailable ? (
                    <p className="text-[14px] text-slate-600">—</p>
                  ) : (
                    <>
                      <p className="text-[14px] font-semibold text-slate-100">{fmt(h.totalValue)}</p>
                      <div className="flex items-center justify-end gap-1.5 mt-1">
                        <span className={`text-[12px] font-medium ${h.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {h.display.pnl >= 0 ? '+' : ''}{fmtDisplay(h.display.pnl)}
                        </span>
                        <PnlBadge value={h.display.pnlPercent} />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Second row: amount · 24h change */}
              <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-700/40">
                <span className="text-[12px] text-slate-400">
                  <span className="font-mono">{formatAmount(h.amount)}</span>{' '}
                  <span className="text-slate-500">{h.symbol}</span>
                </span>
                {priceUnavailable ? (
                  <span className="text-[12px] text-slate-600">— <span className="text-slate-500 font-normal">24h</span></span>
                ) : (
                  <span className={`text-[12px] font-medium ${h.change24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatPercent(h.change24h)} <span className="text-slate-500 font-normal">24h</span>
                  </span>
                )}
              </div>

              {/* Wallets footer — only when transactions are available and there are any */}
              {showWallets && (walletsMap[h.coinId]?.length ?? 0) > 0 && (
                <div className="mt-3">
                  {!canPlan ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openUpgradePrompt(FEATURE_GATES.multiWallet); }}
                      title="Multi-wallet allocation is a Plus feature"
                      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none whitespace-nowrap transition-colors"
                      style={{ background: 'rgba(212,180,131,0.12)', border: '0.5px solid rgba(212,180,131,0.32)', color: '#b89968' }}
                    >
                      <Lock className="w-2.5 h-2.5" />
                      Pro
                    </button>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {wallets.map((w) => (
                        <span
                          key={w}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700/60 border border-slate-600/40 text-slate-400 font-medium leading-none whitespace-nowrap"
                        >
                          {w}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sorted.length === 0 && filterLabel && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Coins className="w-5 h-5 text-slate-700 mb-3" />
          <p className="text-sm text-slate-500">No holdings in {filterLabel}</p>
          <p className="text-[12px] text-[#444444] mt-1">Try a different portfolio or “All Portfolios”</p>
        </div>
      )}
    </div>
  );
}
